import { useState } from "react"
import type { FormEvent } from "react"
import { CheckCircle2 } from "lucide-react"

import { Button } from "@/components/marketing/elements/button"
import { Section } from "@/components/marketing/elements/section"
import { MarketingAnchor } from "@/components/marketing/lib/anchor"
import { waitlistHref, waitlistLabel } from "@/lib/launch-config"
import { joinWaitlist } from "@/lib/marketing-rpc"
import { usePageTitle } from "@/lib/use-page-title"
import { cn } from "@/lib/utils"

import { Field, fieldClassName, joinWaitlistErrorMessage, useAccessFormLayout } from "./access-form"

export function RequestAccessPage() {
  usePageTitle("Request access")

  const Layout = useAccessFormLayout()

  const [email, setEmail] = useState("")
  const [name, setName] = useState("")
  const [company, setCompany] = useState("")
  const [role, setRole] = useState("")
  const [useCase, setUseCase] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError(null)
    setLoading(true)

    const { error: rpcError } = await joinWaitlist({ email, list: "access_request", name, company, role, useCase })

    setLoading(false)

    if (rpcError) {
      setError(joinWaitlistErrorMessage(rpcError))
      return
    }

    setDone(true)
  }

  return (
    <Layout>
      <Section
        id="request-access"
        eyebrow="Limited availability"
        headline="Request access"
        subheadline={<p>We&apos;re opening ArtlessDev to a limited number of teams. Tell us about yours and we&apos;ll be in touch when a place is available.</p>}
      >
        <div className="max-w-xl">
          {done ? (
            <div className="flex items-center gap-3 rounded-xl bg-verified/10 px-6 py-5 text-verified">
              <CheckCircle2 className="size-5 shrink-0" />
              <p className="text-base/6">Request received. We&apos;ll email you when a place opens for your team.</p>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              className="flex flex-col gap-5 rounded-xl bg-mauve-950/2.5 p-6 sm:p-8 dark:bg-white/5"
            >
              <Field label="Name" optional>
                <input
                  className={fieldClassName}
                  type="text"
                  name="name"
                  autoComplete="name"
                  placeholder="Jane Doe"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  disabled={loading}
                />
              </Field>
              <Field label="Work email">
                <input
                  className={fieldClassName}
                  type="email"
                  name="email"
                  autoComplete="email"
                  required
                  placeholder="jane@company.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  disabled={loading}
                />
              </Field>
              <Field label="Company" optional>
                <input
                  className={fieldClassName}
                  type="text"
                  name="company"
                  autoComplete="organization"
                  placeholder="Acme Inc."
                  value={company}
                  onChange={(event) => setCompany(event.target.value)}
                  disabled={loading}
                />
              </Field>
              <Field label="Role" optional>
                <input
                  className={fieldClassName}
                  type="text"
                  name="role"
                  autoComplete="organization-title"
                  placeholder="Engineering, Product, QA…"
                  value={role}
                  onChange={(event) => setRole(event.target.value)}
                  disabled={loading}
                />
              </Field>
              <Field label="What would you use ArtlessDev for?" optional>
                <textarea
                  className={cn(fieldClassName, "min-h-28 resize-y")}
                  name="use_case"
                  placeholder="Tell us about your team and what you're hoping to track."
                  value={useCase}
                  onChange={(event) => setUseCase(event.target.value)}
                  disabled={loading}
                />
              </Field>
              {error ? <p className="text-sm text-destructive">{error}</p> : null}
              <Button type="submit" disabled={loading} size="lg" className="self-start">
                {loading ? "Requesting…" : "Request access"}
              </Button>
            </form>
          )}
          <p className="mt-6 text-sm/6 text-mauve-600 dark:text-mauve-400">
            Not ready yet?{" "}
            <MarketingAnchor href={waitlistHref()} className="font-medium underline underline-offset-4">
              {waitlistLabel()}
            </MarketingAnchor>
          </p>
        </div>
      </Section>
    </Layout>
  )
}
